import React, { useState, useEffect, useRef } from 'react';
import { useToast } from '../../context/ToastContext';
import { Card, Button, Badge, SectionHeader, Spinner, Modal } from '../../components/shared/UI';
import API from '../../utils/apiClient';

const CATEGORIES  = ['grammar','vocabulary','exercises','other'];
const CAT_LABELS  = { grammar:'Grammar', vocabulary:'Vocabulary', exercises:'Exercises', other:'Other' };
const CAT_ICONS   = { grammar:'📐', vocabulary:'📖', exercises:'✏️', other:'📎' };
const LEVELS      = ['A1','A2','B1','B2'];

const emptyForm = { title:'', description:'', level:'A1', category:'grammar' };

export default function AdminMaterialsPage() {
  const { addToast } = useToast();
  const fileRef = useRef();

  const [materials, setMaterials] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [form, setForm]           = useState(emptyForm);
  const [file, setFile]           = useState(null);
  const [saving, setSaving]       = useState(false);
  const [deleteModal, setDeleteModal] = useState(null);
  const [deleting, setDeleting]   = useState(false);
  const [levelFilter, setLevelFilter] = useState('all');

  const load = () => {
    API.get('/materials')
      .then(({ data }) => setMaterials(data.materials || []))
      .catch(() => addToast('Failed to load materials', 'error'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const setF = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleUpload = async () => {
    if (!form.title.trim()) { addToast('Title is required', 'error'); return; }
    if (!file)               { addToast('Please select a file', 'error'); return; }

    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('title',       form.title.trim());
      fd.append('description', form.description);
      fd.append('level',       form.level);
      fd.append('category',    form.category);
      fd.append('file',        file);

      await API.post('/materials', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      addToast('Material uploaded successfully!', 'success');
      setForm(emptyForm);
      setFile(null);
      if (fileRef.current) fileRef.current.value = '';
      load();
    } catch (err) {
      addToast(err.message || 'Upload failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteModal) return;
    setDeleting(true);
    try {
      await API.delete(`/materials/${deleteModal.id}`);
      addToast('Material deleted', 'success');
      setMaterials(p => p.filter(m => m.id !== deleteModal.id));
      setDeleteModal(null);
    } catch (err) {
      addToast(err.message || 'Delete failed', 'error');
    } finally {
      setDeleting(false);
    }
  };

  const filtered = levelFilter === 'all' ? materials : materials.filter(m => m.level === levelFilter);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-8">

      {/* Upload form */}
      <Card className="p-6" accent="red">
        <SectionHeader title="Upload Material" subtitle="Add PDF, Word, or image files for students" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
          <div className="sm:col-span-2">
            <label className="block text-sm font-bold text-de-black mb-1.5">Title *</label>
            <input type="text" value={form.title} onChange={e => setF('title', e.target.value)}
              placeholder="e.g. A1 Grammar — Articles Worksheet"
              className="w-full px-4 py-2.5 text-sm border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none" />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-sm font-bold text-de-black mb-1.5">Description</label>
            <textarea value={form.description} onChange={e => setF('description', e.target.value)}
              placeholder="Brief description of this material..."
              rows={2}
              className="w-full px-4 py-2.5 text-sm border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none resize-none" />
          </div>
          <div>
            <label className="block text-sm font-bold text-de-black mb-1.5">Level</label>
            <select value={form.level} onChange={e => setF('level', e.target.value)}
              className="w-full px-4 py-2.5 text-sm border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none bg-white">
              {LEVELS.map(l => <option key={l} value={l}>Level {l}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-bold text-de-black mb-1.5">Category</label>
            <select value={form.category} onChange={e => setF('category', e.target.value)}
              className="w-full px-4 py-2.5 text-sm border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none bg-white">
              {CATEGORIES.map(c => <option key={c} value={c}>{CAT_ICONS[c]} {CAT_LABELS[c]}</option>)}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="block text-sm font-bold text-de-black mb-1.5">File *</label>
            <input ref={fileRef} type="file"
              accept=".pdf,.doc,.docx,.ppt,.pptx,.jpg,.jpeg,.png"
              onChange={e => setFile(e.target.files[0] || null)}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-semibold file:bg-brand-50 file:text-brand-600 hover:file:bg-brand-100 cursor-pointer" />
            <p className="text-xs text-gray-400 mt-1">Accepted: PDF, Word (.docx), PowerPoint, Images. Max 50MB.</p>
            {file && (
              <p className="text-xs text-green-600 font-medium mt-1">
                ✓ {file.name} ({(file.size / 1024 / 1024).toFixed(1)} MB)
              </p>
            )}
          </div>
        </div>
        <div className="flex justify-end mt-4">
          <Button variant="primary" loading={saving} onClick={handleUpload}>
            ↑ Upload Material
          </Button>
        </div>
      </Card>

      {/* Materials table */}
      <Card className="overflow-hidden">
        <div className="p-5 border-b border-gray-200 flex items-center justify-between flex-wrap gap-3">
          <SectionHeader title={`All Materials (${materials.length})`} />
          <div className="flex gap-1 p-1 bg-surface rounded-xl">
            {['all', ...LEVELS].map(lv => (
              <button key={lv} onClick={() => setLevelFilter(lv)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all
                  ${levelFilter === lv ? 'bg-white text-brand-500 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                {lv === 'all' ? 'All' : lv}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-10"><Spinner className="text-brand-500" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-10 text-gray-400 text-sm">No materials yet for this level.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-surface border-b border-gray-200">
                <tr>
                  {['Title','Level','Category','Size','Uploaded','Actions'].map(h => (
                    <th key={h} className="text-left py-3 px-4 text-xs font-bold text-gray-500 uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(m => (
                  <tr key={m.id} className="border-b border-gray-50 hover:bg-surface transition-colors">
                    <td className="py-3 px-4">
                      <p className="font-semibold text-de-black text-xs">{m.title}</p>
                      {m.description && <p className="text-xs text-gray-400 mt-0.5 truncate max-w-xs">{m.description}</p>}
                    </td>
                    <td className="py-3 px-4"><Badge variant="gold" size="xs">{m.level}</Badge></td>
                    <td className="py-3 px-4 text-xs text-gray-600 capitalize">{CAT_ICONS[m.category]} {m.category}</td>
                    <td className="py-3 px-4 text-xs text-gray-500">{m.file_size || '—'}</td>
                    <td className="py-3 px-4 text-xs text-gray-400">
                      {new Date(m.created_at).toLocaleDateString('en-GB')}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex gap-2">
                        <a href={m.file_url} target="_blank" rel="noopener noreferrer">
                          <Button size="sm" variant="secondary">↓ View</Button>
                        </a>
                        <Button size="sm" variant="danger" onClick={() => setDeleteModal(m)}>Delete</Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Delete confirm */}
      <Modal isOpen={!!deleteModal} onClose={() => setDeleteModal(null)} title="Delete Material" size="sm">
        {deleteModal && (
          <div>
            <p className="text-sm text-de-text mb-4">
              Are you sure you want to delete <strong>"{deleteModal.title}"</strong>? This cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <Button variant="secondary" onClick={() => setDeleteModal(null)}>Cancel</Button>
              <Button variant="danger" loading={deleting} onClick={handleDelete}>Delete</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
